import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { uppercaseLabel } from '../styles/typography';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Link,
    Popover,
    Menu,
    MenuItem,
    Snackbar,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SyncIcon from '@mui/icons-material/Sync';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PowerOffIcon from '@mui/icons-material/PowerOff';

import { callBackend } from '../utils/api';
import {
    secondaryActionButtonSx,
    secondaryModalSx,
    secondaryPanelSx,
} from '../styles/secondaryMsiStyles';

const emptyStatus = {
    connected: false,
    intraday_enabled: false,
    baseline_query_id: '',
    intraday_query_id: '',
    historical_query_id: '',
    last_baseline_sync_at: null,
    last_baseline_sync_status: null,
    last_baseline_sync_error: null,
    last_intraday_sync_at: null,
    last_intraday_sync_status: null,
    last_intraday_sync_error: null,
    last_historical_sync_at: null,
    last_historical_sync_status: null,
    last_historical_sync_error: null,
};

const formatDateTime = (value) => {
    if (!value) return 'Never';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
};

export default function IbkrConnectCard({
    onSyncSuccess,
    onStatusChange,
    compact = false,
    hideTitle = false,
    hideStatusSummary = false,
    title = 'Connect Interactive Brokers',
    syncActionsOnly = false,
}) {
    const [status, setStatus] = useState(emptyStatus);
    const [statusLoading, setStatusLoading] = useState(false);
    const [learnMoreAnchor, setLearnMoreAnchor] = useState(null);
    const [connectOpen, setConnectOpen] = useState(false);
    const [syncMenuAnchor, setSyncMenuAnchor] = useState(null);
    const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);
    const [pendingSyncMode, setPendingSyncMode] = useState(null);

    const [flexToken, setFlexToken] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [baselineQueryId, setBaselineQueryId] = useState('');
    const [intradayEnabled, setIntradayEnabled] = useState(false);
    const [intradayQueryId, setIntradayQueryId] = useState('');
    const [historicalQueryId, setHistoricalQueryId] = useState('');

    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testError, setTestError] = useState('');
    const [syncing, setSyncing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const extractErrorMessage = useCallback((error) => {
        const raw = String(error?.message || error || '');
        const parts = raw.split(' - ');
        if (parts.length >= 2) {
            const maybeJson = parts.slice(1).join(' - ');
            try {
                const parsed = JSON.parse(maybeJson);
                if (parsed?.detail) return parsed.detail;
            } catch {
                return maybeJson;
            }
        }
        return raw || 'Request failed.';
    }, []);

    const refreshStatus = useCallback(async () => {
        setStatusLoading(true);
        try {
            const resp = await callBackend('api/ibkr/status', 'GET');
            const nextStatus = { ...emptyStatus, ...resp };
            setStatus(nextStatus);
            onStatusChange?.(nextStatus);
        } catch (err) {
            console.error('Failed to fetch IBKR status:', err);
        } finally {
            setStatusLoading(false);
        }
    }, [onStatusChange]);

    useEffect(() => {
        refreshStatus();
    }, [refreshStatus]);

    useEffect(() => {
        setTestResult(null);
        setTestError('');
    }, [flexToken, baselineQueryId, intradayEnabled, intradayQueryId, historicalQueryId]);

    const openConnectModal = () => {
        setFlexToken('');
        setShowToken(false);
        setBaselineQueryId(status.baseline_query_id || '');
        setIntradayEnabled(Boolean(status.intraday_enabled));
        setIntradayQueryId(status.intraday_query_id || '');
        setHistoricalQueryId(status.historical_query_id || '');
        setTestResult(null);
        setTestError('');
        setConnectOpen(true);
    };

    const baselineSyncLine = useMemo(() => {
        if (!status.connected) return null;
        return `${formatDateTime(status.last_baseline_sync_at)} (${status.last_baseline_sync_status || 'unknown'})`;
    }, [status]);

    const intradaySyncLine = useMemo(() => {
        if (!status.connected || !status.intraday_enabled) return null;
        return `${formatDateTime(status.last_intraday_sync_at)} (${status.last_intraday_sync_status || 'unknown'})`;
    }, [status]);

    const historicalSyncLine = useMemo(() => {
        if (!status.connected || !status.historical_query_id) return null;
        return `${formatDateTime(status.last_historical_sync_at)} (${status.last_historical_sync_status || 'unknown'})`;
    }, [status]);

    const hasEffectiveToken = Boolean(flexToken.trim() || status.connected);
    const hasBaselineQuery = Boolean(baselineQueryId.trim());
    const canTest = Boolean(hasEffectiveToken && hasBaselineQuery);
    const canSave = Boolean(
        hasEffectiveToken &&
        hasBaselineQuery &&
        (!intradayEnabled || intradayQueryId.trim())
    );

    const runTest = async () => {
        if (!canTest) return;
        setTesting(true);
        setTestError('');
        setTestResult(null);
        try {
            const shouldTestIntraday = Boolean(intradayEnabled && intradayQueryId.trim());
            const resp = await callBackend('api/ibkr/connect', 'POST', {
                flex_token: flexToken.trim() || null,
                baseline_query_id: baselineQueryId.trim(),
                intraday_enabled: shouldTestIntraday,
                intraday_query_id: shouldTestIntraday ? intradayQueryId.trim() : null,
                historical_query_id: historicalQueryId.trim() || null,
                dry_run: true,
            });
            setTestResult({
                ok: true,
                baselineCount: resp?.baseline_positions_count ?? 0,
                intradayCount: resp?.intraday_executions_count,
                historicalCount: resp?.historical_executions_count,
            });
        } catch (err) {
            const message = extractErrorMessage(err);
            setTestError(message);
            setTestResult({ ok: false });
        } finally {
            setTesting(false);
        }
    };

    const saveSettings = async () => {
        if (!canSave) return;
        setSaving(true);
        setTestError('');
        try {
            await callBackend('api/ibkr/connect', 'POST', {
                flex_token: flexToken.trim() || null,
                baseline_query_id: baselineQueryId.trim(),
                intraday_enabled: intradayEnabled,
                intraday_query_id: intradayQueryId.trim() || null,
                historical_query_id: historicalQueryId.trim() || null,
            });
            setConnectOpen(false);
            setSnackbar({ open: true, message: 'IBKR settings saved.', severity: 'success' });
            await refreshStatus();
        } catch (err) {
            setTestError(extractErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            await callBackend('api/ibkr/disconnect', 'POST');
            setSnackbar({ open: true, message: 'IBKR disconnected.', severity: 'success' });
            setStatus(emptyStatus);
            onStatusChange?.(emptyStatus);
        } catch (err) {
            setSnackbar({ open: true, message: extractErrorMessage(err), severity: 'error' });
        }
    };

    const handleSync = async (mode) => {
        setSyncing(true);
        try {
            if (mode === 'historical') {
                const resp = await callBackend('api/ibkr/sync-history', 'POST', {});
                setSnackbar({
                    open: true,
                    message: `Historical sync complete (${resp?.executions_upserted ?? 0} executions processed).`,
                    severity: 'success',
                });
                await onSyncSuccess?.(resp);
            } else {
                const resp = await callBackend('api/ibkr/sync', 'POST', { mode });
                setSnackbar({ open: true, message: 'Import staged. Review and apply to update holdings.', severity: 'success' });
                await onSyncSuccess?.(resp);
            }
            await refreshStatus();
        } catch (err) {
            setSnackbar({ open: true, message: extractErrorMessage(err), severity: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    const openSyncConfirm = (mode) => {
        setPendingSyncMode(mode);
        setConfirmSyncOpen(true);
    };

    const handleConfirmSync = () => {
        if (!pendingSyncMode) return;
        setConfirmSyncOpen(false);
        setPendingSyncMode(null);
        handleSync(pendingSyncMode);
    };

    const handleCancelSync = () => {
        setConfirmSyncOpen(false);
        setPendingSyncMode(null);
    };

    const syncButtonDisabled = !status.connected || syncing;
    const compactButtonSx = compact
        ? {
            minHeight: 28,
            px: 1.15,
            borderRadius: 999,
            fontSize: '0.68rem',
            ...uppercaseLabel({
              letterSpacing: '0.06em',
            }),
            boxShadow: 'none',
            ...secondaryActionButtonSx,
          }
        : undefined;
    const compactIconSx = compact
        ? {
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }
        : undefined;
    const baselineSyncTooltip = !status.connected
        ? 'Connect IBKR to enable sync.'
        : [
            `Baseline ${baselineSyncLine || 'Never'}`,
            status.last_baseline_sync_error ? `Error: ${status.last_baseline_sync_error}` : null,
        ].filter(Boolean).join(' • ');
    const intradaySyncTooltip = !status.connected
        ? 'Connect IBKR to enable sync.'
        : [
            `Baseline ${baselineSyncLine || 'Never'}`,
            status.intraday_enabled ? `Intraday ${intradaySyncLine || 'Never'}` : null,
            status.historical_query_id ? `History ${historicalSyncLine || 'Never'}` : null,
            status.last_baseline_sync_error ? `Baseline error: ${status.last_baseline_sync_error}` : null,
            status.last_intraday_sync_error ? `Intraday error: ${status.last_intraday_sync_error}` : null,
            status.last_historical_sync_error ? `History error: ${status.last_historical_sync_error}` : null,
        ].filter(Boolean).join(' • ');

    const renderSyncControls = () => {
        if (!status.connected) {
            return hideStatusSummary ? null : (
                <Box sx={{ mt: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {syncActionsOnly ? 'Configure IBKR in User Settings to enable sync.' : 'Edit IBKR settings to sync.'}
                    </Typography>
                </Box>
            );
        }

        if (status.intraday_enabled) {
            return (
                <Box sx={{ mt: 0, display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={intradaySyncTooltip}>
                        <span>
                            <Button
                                variant={compact ? 'outlined' : 'contained'}
                                size="small"
                                startIcon={
                                    syncing ? (
                                        <CircularProgress size={14} color="inherit" />
                                    ) : (
                                        <SyncIcon fontSize="small" />
                                    )
                                }
                                onClick={() => openSyncConfirm('intraday')}
                                disabled={syncButtonDisabled}
                                sx={compactButtonSx}
                            >
                                {syncing ? 'Syncing...' : 'Intraday'}
                            </Button>
                        </span>
                    </Tooltip>
                    <IconButton
                        size="small"
                        onClick={(event) => setSyncMenuAnchor(event.currentTarget)}
                        disabled={syncButtonDisabled}
                        sx={{ ml: 0.5, ...(compactIconSx || {}) }}
                    >
                        ▾
                    </IconButton>
                        <Menu
                        anchorEl={syncMenuAnchor}
                        open={Boolean(syncMenuAnchor)}
                        onClose={() => setSyncMenuAnchor(null)}
                    >
                        <MenuItem
                            onClick={() => { setSyncMenuAnchor(null); openSyncConfirm('baseline'); }}
                            disabled={syncButtonDisabled}
                        >
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <SyncIcon fontSize="small" />
                                <span>EOD</span>
                            </Box>
                        </MenuItem>
                        <MenuItem
                            onClick={() => { setSyncMenuAnchor(null); openSyncConfirm('historical'); }}
                            disabled={syncButtonDisabled || !status.historical_query_id}
                        >
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <SyncIcon fontSize="small" />
                                <span>History</span>
                            </Box>
                        </MenuItem>
                        </Menu>
                    </Box>
                );
        }

        return (
            <Box sx={{ mt: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={baselineSyncTooltip}>
                    <span>
                        <Button
                            variant={compact ? 'outlined' : 'contained'}
                            size="small"
                            startIcon={
                                syncing ? (
                                    <CircularProgress size={14} color="inherit" />
                                ) : (
                                    <SyncIcon fontSize="small" />
                                )
                            }
                            onClick={() => openSyncConfirm('baseline')}
                            disabled={syncButtonDisabled}
                            sx={compactButtonSx}
                        >
                            {syncing ? 'Syncing...' : 'EOD'}
                        </Button>
                    </span>
                </Tooltip>
                {status.historical_query_id && (
                    <Tooltip title="Sync historical executions">
                        <span>
                            <Button
                                variant={compact ? 'outlined' : 'contained'}
                                size="small"
                                onClick={() => openSyncConfirm('historical')}
                                disabled={syncButtonDisabled}
                                sx={compactButtonSx}
                            >
                                History
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Box>
        );
    };

    const statusText = statusLoading ? 'Checking...' : status.connected ? 'Connected' : 'Not connected';
    const statusDotColor = statusLoading ? 'grey.400' : status.connected ? 'success.main' : 'error.main';

    return (
        <>
            {!hideTitle && (
                <>
                    {compact ? (
                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 0.25 }}>
                            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Typography variant="subtitle2">{title}</Typography>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: statusDotColor,
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        {statusText}
                                    </Typography>
                                </Box>
                            </Box>
                            <Tooltip title="About IBKR sync">
                                <IconButton
                                    size="small"
                                    onClick={(event) => setLearnMoreAnchor(event.currentTarget)}
                                    sx={{ color: 'text.secondary', p: 0.2, mt: -0.2 }}
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            {title}
                        </Typography>
                    )}
                </>
            )}
            <Box sx={{ width: '100%' }}>
                {!compact && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {statusText}
                    </Typography>
                )}
                {status.connected && !hideStatusSummary && (
                    <>
                        <Typography variant="caption" display="block" sx={{ fontSize: compact ? '0.68rem' : undefined, lineHeight: 1.35 }}>
                            Baseline sync: {baselineSyncLine}
                        </Typography>
                        {status.last_baseline_sync_error && (
                            <Typography variant="caption" color="error" display="block" sx={{ fontSize: compact ? '0.68rem' : undefined, lineHeight: 1.35 }}>
                                Baseline error: {status.last_baseline_sync_error}
                            </Typography>
                        )}
                        {status.intraday_enabled && (
                            <>
                                <Typography variant="caption" display="block" sx={{ fontSize: compact ? '0.68rem' : undefined, lineHeight: 1.35 }}>
                                    Intraday sync: {intradaySyncLine}
                                </Typography>
                                {status.last_intraday_sync_error && (
                                    <Typography variant="caption" color="error" display="block" sx={{ fontSize: compact ? '0.68rem' : undefined, lineHeight: 1.35 }}>
                                        Intraday error: {status.last_intraday_sync_error}
                                    </Typography>
                                )}
                            </>
                        )}
                        {status.historical_query_id && (
                            <>
                                <Typography variant="caption" display="block" sx={{ fontSize: compact ? '0.68rem' : undefined, lineHeight: 1.35 }}>
                                    History sync: {historicalSyncLine}
                                </Typography>
                                {status.last_historical_sync_error && (
                                    <Typography variant="caption" color="error" display="block" sx={{ fontSize: compact ? '0.68rem' : undefined, lineHeight: 1.35 }}>
                                        History error: {status.last_historical_sync_error}
                                    </Typography>
                                )}
                            </>
                        )}
                    </>
                )}

                <Box display="flex" alignItems="center" gap={1} sx={{ mt: hideStatusSummary ? 0 : 1, flexWrap: 'nowrap' }}>
                    {renderSyncControls()}
                    {!syncActionsOnly && (
                        <>
                            <Tooltip title="Edit IBKR Settings">
                                <span>
                                    <IconButton size="small" color="primary" onClick={openConnectModal} sx={compactIconSx}>
                                        <EditNoteIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {status.connected && (
                                <Tooltip title="Disconnect from IBKR">
                                    <span>
                                        <IconButton size="small" color="error" onClick={handleDisconnect} sx={compactIconSx}>
                                            <PowerOffIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </>
                    )}
                </Box>

                {!compact && !syncActionsOnly && (
                    <Box sx={{ mt: 1 }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={(event) => setLearnMoreAnchor(event.currentTarget)}
                        >
                            Learn more
                        </Link>
                    </Box>
                )}
                <Popover
                    open={Boolean(learnMoreAnchor)}
                    anchorEl={learnMoreAnchor}
                    onClose={() => setLearnMoreAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{ sx: { p: 1.5, maxWidth: 320, ...secondaryPanelSx } }}
                >
                    <Typography variant="body2" color="text.secondary">
                        IBKR’s reporting integration uses a Flex Token and up to three Flex Query IDs. Baseline provides
                        prior-day positions, Intraday adds today’s executed trades, and History backfills past executions.
                    </Typography>
                </Popover>
            </Box>

            <Dialog
                open={connectOpen && !syncActionsOnly}
                onClose={() => setConnectOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: secondaryModalSx }}
            >
                <DialogTitle>IBKR Connect</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">Step 1 — Flex Web Service Token (Required)</Typography>
                        <TextField
                            label="Flex Token"
                            type={showToken ? 'text' : 'password'}
                            value={flexToken}
                            onChange={(e) => setFlexToken(e.target.value)}
                            fullWidth
                            margin="dense"
                            helperText={status.connected
                                ? "Leave blank to use your currently saved token, or paste a new token to rotate it."
                                : "Generate in IBKR Portal → Reporting → Flex Queries → Flex Web Service (gear icon) → Generate Token"}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setShowToken((prev) => !prev)}>
                                            {showToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard.writeText(flexToken)}
                                            disabled={!flexToken}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                            Keep this token private. It grants read-only access to your reports.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">Step 2 — Baseline Portfolio Query (Required)</Typography>
                        <TextField
                            label="Baseline (Prior-day Positions) Query ID"
                            value={baselineQueryId}
                            onChange={(e) => setBaselineQueryId(e.target.value)}
                            fullWidth
                            margin="dense"
                            helperText="Activity Flex Query • Include section: Open Positions • Period: Last Business Day • Format: XML"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard.writeText(baselineQueryId)}
                                            disabled={!baselineQueryId}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="caption">Where do I find the Query ID?</Typography>
                            <Tooltip title="In Flex Queries list, click the ℹ️ icon next to your query.">
                                <HelpOutlineIcon fontSize="small" color="action" />
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">Step 3 — Intraday Updates (Optional, recommended)</Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={intradayEnabled}
                                    onChange={(e) => setIntradayEnabled(e.target.checked)}
                                />
                            }
                            label="Enable intraday updates (recommended)"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Baseline pulls your portfolio as of the last close. Intraday pulls today’s trades. We combine
                            them so you can see near-real-time holdings.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Intraday updates can lag by a few minutes after a trade executes in IBKR.
                        </Typography>

                        {intradayEnabled && (
                            <>
                                <TextField
                                    label="Intraday (Today’s Executions) Query ID"
                                    value={intradayQueryId}
                                    onChange={(e) => setIntradayQueryId(e.target.value)}
                                    fullWidth
                                    margin="dense"
                                    helperText="Trade Confirmations Flex Query • Include section: Executions (fills) • Period: Today • Format: XML"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigator.clipboard.writeText(intradayQueryId)}
                                                    disabled={!intradayQueryId}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="caption">Why do I need this?</Typography>
                                    <Tooltip title="IBKR Flex doesn’t provide a single live positions report. We combine yesterday’s positions with today’s executions to estimate current holdings.">
                                        <HelpOutlineIcon fontSize="small" color="action" />
                                    </Tooltip>
                                </Box>
                                {!intradayQueryId.trim() && (
                                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                        Required when intraday updates are enabled.
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">Step 4 — Historical Trade Query (Optional, recommended)</Typography>
                        <TextField
                            label="Historical Trades Query ID"
                            value={historicalQueryId}
                            onChange={(e) => setHistoricalQueryId(e.target.value)}
                            fullWidth
                            margin="dense"
                            helperText="Activity Flex Query • Trades section • Level: Executions • XML format"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard.writeText(historicalQueryId)}
                                            disabled={!historicalQueryId}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                            Configure this query to enable historical trade backfills for performance.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2">Step 5 — Test & Save</Typography>
                        <Box display="flex" gap={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                            <Button variant="outlined" onClick={runTest} disabled={!canTest || testing}>
                                {testing ? 'Testing...' : 'Test Connection'}
                            </Button>
                            <Button variant="contained" onClick={saveSettings} disabled={!canSave || saving}>
                                {saving ? 'Saving...' : 'Save Settings'}
                            </Button>
                            <Button onClick={() => setConnectOpen(false)}>Cancel</Button>
                        </Box>

                        {testResult?.ok && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Baseline query: OK ({testResult.baselineCount} positions found)
                                </Typography>
                                {intradayEnabled && intradayQueryId.trim() && (
                                    <Typography variant="body2">
                                        Intraday query: OK ({testResult.intradayCount ?? 0} executions found today)
                                    </Typography>
                                )}
                                {intradayEnabled && !intradayQueryId.trim() && (
                                    <Typography variant="body2" color="warning.main">
                                        Intraday query skipped (no Query ID entered).
                                    </Typography>
                                )}
                                {historicalQueryId.trim() && (
                                    <Typography variant="body2">
                                        Historical query: OK ({testResult.historicalCount ?? 0} executions found)
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {testError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {testError}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={confirmSyncOpen}
                onClose={handleCancelSync}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: secondaryModalSx }}
            >
                <DialogTitle>Confirm Sync</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">
                        {pendingSyncMode === 'historical'
                            ? 'Are you sure? This will fetch and upsert historical executions for performance calculations.'
                            : 'Are you sure? This will overwrite your existing portfolio.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelSync}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmSync} disabled={syncing}>
                        Sync
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
